import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

import { clientService, ClientWithScrapingTimes, Service, RefreshStatus, ClientType } from '@/services/client.service';

export interface Client {
  id: string;
  name: string | null;
  panId: string;
  email?: string | null;
  phone?: string | null;
  password: string;
  updatedAt: string;
  clientType?: ClientType;
  businessType?: 'AUDITED' | 'NON_AUDITED';
  loginStatus?: 'SUCCESSFUL' | 'INCORRECT_USERNAME' | 'INCORRECT_PASSWORD' | 'PAN_NOT_EXISTING' | null;
  isVerified?: boolean;
  scrapingTimes?: Array<{
    jobId: string;
    jobType: 'FYA' | 'DEMANDS' | 'ITR' | 'FYI' | 'AIS_TIS';
    lastSuccessfulRun: string | null;
    lastRunStatus: 'SUCCESS' | 'FAILED' | 'PROCESSING' | 'PENDING' | null;
    lastRunDate: string | null;
  }>;
  refreshStatus?: RefreshStatus;
  dateOfBirth?: string | null;
  gender?: string | null;
  panStatus?: string | null;
  aadhaarLinked?: boolean | null;
  address?: string | null;
  profileLastSyncedAt?: string | null;
  // Client portal login (enabled by the accountant)
  loginEmail?: string | null;
  loginEnabled?: boolean;
  loginEnabledAt?: string | null;
  loginInvitedAt?: string | null;
  clientLastLoginAt?: string | null;
}

interface ApiError {
  response?: {
    data?: {
      error?: string;
      errors?:
        | {
            formErrors?: string[];
            fieldErrors?: Record<string, string[]>;
          }
        | Record<string, unknown>;
    };
  };
  data?: {
    error?: string;
  };
  message: string;
}

interface GetClientsResponse {
  clients: ClientWithScrapingTimes[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  permissions: {
    canAddClients: boolean;
    currentClientCount: number;
    clientLimit: number;
    isSubscriptionActive: boolean;
    subscriptionType?: string;
  };
}

export interface FailedRecord {
  row: number;
  name: string;
  panId: string;
  error: string;
}

interface ClientsState {
  clients: Client[];
  services: Service[];
  isLoading: boolean;
  error: string | null;
  createClientError: string | null;
  updateClientError: string | null;
  bulkImport: {
    isLoading: boolean;
    inProgress: boolean;
    success: boolean;
    error: string | null;
    summary: {
      total: number;
      successful: number;
      failed: number;
      failedRecords: FailedRecord[];
    } | null;
  };
  permissions: GetClientsResponse['permissions'];
  pagination: GetClientsResponse['pagination'];
}

const initialState: ClientsState = {
  clients: [],
  services: [],
  isLoading: false,
  error: null,
  createClientError: null,
  updateClientError: null,
  bulkImport: {
    isLoading: false,
    inProgress: false,
    success: false,
    error: null,
    summary: null,
  },
  permissions: {
    canAddClients: true, // Default to true until we get data from the API
    currentClientCount: 0,
    clientLimit: 0,
    isSubscriptionActive: true,
    subscriptionType: undefined,
  },
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  },
};

// Async thunk for fetching services
export const fetchServices = createAsyncThunk(
  'clients/fetchServices',
  async (_, { rejectWithValue }) => {
    try {
      const response = await clientService.getServices();
      return response.services;
    } catch (error) {
      const apiError = error as ApiError;
      return rejectWithValue({
        error: apiError.response?.data?.error || apiError.data?.error || apiError.message,
      });
    }
  },
);

// Async thunk for fetching clients
export const fetchClients = createAsyncThunk(
  'clients/fetchClients',
  async ({
    page,
    limit,
    search,
    clientType,
    verificationFailed,
    refreshFailed,
    verifiedRefreshFailed,
  }: {
    page: number;
    limit: number;
    search?: string;
    clientType?: ClientType;
    verificationFailed?: boolean;
    refreshFailed?: boolean;
    verifiedRefreshFailed?: boolean;
  }, { rejectWithValue }) => {
    try {
      const response = await clientService.getClients({
        page,
        limit,
        search,
        clientType,
        verificationFailed,
        refreshFailed,
        verifiedRefreshFailed,
      });
      // Ensure all clients have required fields to match our Client interface
      const normalizedClients = response.clients.map((client) => ({
        id: client.id || '',
        name: client.name || '',
        panId: client.panId || '',
        email: client.email,
        phone: client.phone,
        password: client.password || '',
        updatedAt: client.updatedAt || new Date().toISOString(),
        clientType: client.clientType,
        businessType: client.businessType,
        loginStatus: client.loginStatus,
        isVerified: client.isVerified,
        scrapingTimes: client.scrapingTimes || [],
        refreshStatus: client.refreshStatus || { latestSuccessDate: null, lastFailedAttempt: null, isRefreshing: false },
        dateOfBirth: client.dateOfBirth,
        gender: client.gender,
        panStatus: client.panStatus,
        aadhaarLinked: client.aadhaarLinked,
        address: client.address,
        profileLastSyncedAt: client.profileLastSyncedAt,
        loginEmail: client.loginEmail ?? null,
        loginEnabled: client.loginEnabled ?? false,
        loginEnabledAt: client.loginEnabledAt ?? null,
        loginInvitedAt: client.loginInvitedAt ?? null,
        clientLastLoginAt: client.clientLastLoginAt ?? null,
      }));
      return {
        clients: normalizedClients,
        pagination: response.pagination,
        permissions: response.permissions,
      };
    } catch (error) {
      const apiError = error as ApiError;
      return rejectWithValue({
        error: apiError.response?.data?.error || apiError.data?.error || apiError.message,
      });
    }
  },
);

// Async thunk for adding a client
export const createClient = createAsyncThunk(
  'clients/createClient',
  async (
    client: Omit<Client, 'id' | 'updatedAt'> & {
      businessType: 'AUDITED' | 'NON_AUDITED';
      clientType: ClientType;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await clientService.addClient(client);
      // Ensure the returned client has all required fields
      const normalizedClient = {
        ...response.client,
        password: response.client.password || '',
      };
      return normalizedClient;
    } catch (error) {
      const apiError = error as ApiError;

      const fieldErrors = apiError.response?.data?.errors?.fieldErrors;
      if (fieldErrors) {
        const errorMessages = Object.entries(fieldErrors)
          .map(([field, errors]) => `${field}: ${errors.join(', ')}`)
          .join('\n');
        return rejectWithValue({
          error:
            errorMessages ||
            apiError.response?.data?.error ||
            apiError.data?.error ||
            apiError.message,
        });
      }

      return rejectWithValue({
        error: apiError.response?.data?.error || apiError.data?.error || apiError.message,
      });
    }
  },
);

// Async thunk for updating a client
export const updateClient = createAsyncThunk(
  'clients/updateClient',
  async (
    { id, clientData }: { id: string; clientData: Partial<Omit<Client, 'id' | 'updatedAt'>> },
    { rejectWithValue },
  ) => {
    try {
      const response = await clientService.updateClient(id, clientData);
      return response.client;
    } catch (error) {
      const apiError = error as ApiError;
      const fieldErrors = apiError.response?.data?.errors?.fieldErrors;
      if (fieldErrors) {
        const errorMessages = Object.entries(fieldErrors)
          .map(([field, errors]) => `${field}: ${errors.join(', ')}`)
          .join('\n');
        return rejectWithValue({
          error:
            errorMessages ||
            apiError.response?.data?.error ||
            apiError.data?.error ||
            apiError.message,
        });
      }

      return rejectWithValue({
        error: apiError.response?.data?.error || apiError.data?.error || apiError.message,
      });
    }
  },
);

// Async thunk for bulk importing clients
export const bulkImportClients = createAsyncThunk(
  'clients/bulkImportClients',
  async (file: File, { dispatch, rejectWithValue }) => {
    try {
      const response = await clientService.bulkImportClients(file);
      // After successful import, refresh the client list
      dispatch(fetchClients({ page: 1, limit: 10 }));
      return response;
    } catch (error) {
      // Extract error information for the rejected case
      if (error instanceof Error) {
        return rejectWithValue({
          error: error.message,
        });
      }
      const apiError = error as ApiError;
      return rejectWithValue({
        error: apiError.response?.data?.error || apiError.data?.error || apiError.message,
      });
    }
  },
);

// Async thunk for deleting a client
export const deleteClient = createAsyncThunk(
  'clients/deleteClient',
  async (clientId: string, { rejectWithValue }) => {
    try {
      const response = await clientService.deleteClient(clientId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete client');
    }
  },
);

const clientsSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    // Merge the client-login fields returned by PUT /clients/:id/login-access into the list
    applyClientLoginAccess: (
      state,
      action: PayloadAction<
        Pick<Client, 'id' | 'loginEmail' | 'loginEnabled' | 'loginEnabledAt' | 'loginInvitedAt' | 'clientLastLoginAt'>
      >,
    ) => {
      const client = state.clients.find((c) => c.id === action.payload.id);
      if (client) {
        Object.assign(client, action.payload);
      }
    },
    resetBulkImport: (state) => {
      state.bulkImport = {
        isLoading: false,
        inProgress: false,
        success: false,
        error: null,
        summary: null,
      };
    },
    // This is now a local action for testing/mock purposes only
    // The real client creation is handled by the createClient async thunk
    addClient: (state, action: PayloadAction<Omit<Client, 'id' | 'updatedAt'>>) => {
      const newClient = {
        id: Date.now().toString(), // Use timestamp as temporary ID
        name: action.payload.name,
        panId: action.payload.panId,
        email: action.payload.email || '',
        phone: action.payload.phone || '',
        password: action.payload.password,
        updatedAt: new Date().toLocaleString('en-US', {
          day: 'numeric',
          month: 'long',
          hour: 'numeric',
          minute: 'numeric',
          hour12: true,
        }),
      };
      state.clients.push(newClient);
    },
    setClients: (state, action: PayloadAction<Client[]>) => {
      state.clients = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearClientErrors: (state) => {
      state.createClientError = null;
      state.updateClientError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchServices
      .addCase(fetchServices.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchServices.fulfilled, (state, action) => {
        state.services = action.payload;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(fetchServices.rejected, (state, action) => {
        state.isLoading = false;
        // Extract error message from the action payload if available
        const errorObj = action.payload as { error?: string };
        state.error = errorObj?.error || action.error.message || 'Failed to fetch services';
      })
      // Handle fetchClients
      .addCase(fetchClients.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.isLoading = false;
        state.clients = action.payload.clients;
        state.pagination = action.payload.pagination;
        state.permissions = action.payload.permissions;
        state.error = null;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.isLoading = false;
        // Extract error message from the action payload if available
        const errorObj = action.payload as { error?: string };
        state.error = errorObj?.error || action.error.message || 'Failed to fetch clients';
      })
      // Handle createClient
      .addCase(createClient.pending, (state) => {
        state.isLoading = true;
        state.createClientError = null;
      })
      .addCase(createClient.fulfilled, (state, action) => {
        state.clients.push(action.payload);
        state.isLoading = false;
        state.createClientError = null;
      })
      .addCase(createClient.rejected, (state, action) => {
        state.isLoading = false;
        // Extract error message from the action payload if available
        const errorObj = action.payload as { error?: string };
        state.createClientError = errorObj?.error || action.error.message || 'Failed to add client';
      })
      // Handle updateClient
      .addCase(updateClient.pending, (state) => {
        state.isLoading = true;
        state.updateClientError = null;
      })
      .addCase(updateClient.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update the client in the list
        const updatedClient = action.payload;
        const index = state.clients.findIndex((client) => client.id === updatedClient.id);
        if (index !== -1) {
          state.clients[index] = updatedClient;
        }
        state.updateClientError = null;
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.isLoading = false;
        // Extract error message from the action payload if available
        const errorObj = action.payload as { error?: string };
        state.updateClientError =
          errorObj?.error || action.error.message || 'Failed to update client';
      })
      // Handle bulkImportClients
      .addCase(bulkImportClients.pending, (state) => {
        state.bulkImport.isLoading = true;
        state.bulkImport.success = false;
        state.bulkImport.summary = null;
      })
      .addCase(bulkImportClients.fulfilled, (state, action) => {
        state.bulkImport.isLoading = false;
        state.bulkImport.success = true;

        const payload = action.payload;

        if (payload && payload.summary) {
          state.bulkImport.summary = {
            total: payload.summary.total,
            successful: payload.summary.successful,
            failed: payload.summary.failed,
            failedRecords: payload.failedRecords || [],
          };
        } else {
          state.bulkImport.summary = {
            total: 0,
            successful: 0,
            failed: 0,
            failedRecords: [],
          };
        }
      })
      .addCase(bulkImportClients.rejected, (state, action) => {
        state.bulkImport.isLoading = false;
        state.bulkImport.success = false;
        // Extract error message from the action payload if available
        const errorObj = action.payload as { error?: string };
        const errorMessage = errorObj?.error || action.error.message || 'Failed to import clients';
        // Set the error in the state
        state.bulkImport.error = errorMessage;
      })
      // Handle deleteClient
      .addCase(deleteClient.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteClient.fulfilled, (state) => {
        state.isLoading = false;
        // We'll refetch clients after deletion, so no need to update state here
      })
      .addCase(deleteClient.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          typeof action.payload === 'string'
            ? action.payload
            : action.error.message || 'Failed to delete client';
      });
  },
});

export const {
  applyClientLoginAccess,
  resetBulkImport,
  addClient,
  setClients,
  setLoading,
  setError: setClientError,
  clearClientErrors,
} = clientsSlice.actions;

export default clientsSlice.reducer;
